import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

function useDarkMode() {
  const [dark, setDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark",
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setDark(document.documentElement.getAttribute("data-theme") === "dark"),
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return dark;
}

function useIsMobile(bp = 640) {
  const [m, setM] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return m;
}

const FAQS = [
  {
    question: "What is Telemedicine?",
    answer:
      "Telemedicine uses secure video, audio and messaging to connect patients with licensed clinicians remotely — for consultations, prescriptions and follow-ups, with the same care standards as an in-person visit.",
  },
  {
    question: "How does Telemedicine work?",
    answer:
      "Search a specialty, choose a slot and meet your doctor on HD video. Clinical notes, prescriptions and lab orders are delivered directly to your Doxzon account.",
  },
  {
    question: "What are the benefits of Telemedicine?",
    answer:
      "Faster access, lower cost, no commute, full records in one place, and continuity of care across every provider you visit.",
  },
  {
    question: "How can I schedule a Telemedicine appointment?",
    answer:
      "Use the booking widget at the top of this page or your Doxzon mobile app. Same-day and 24/7 urgent care slots are available.",
  },
  {
    question: "Can I get a prescription through Telemedicine?",
    answer:
      "Yes, healthcare providers can issue prescriptions during Telemedicine consultations, which can be sent to your pharmacy electronically.",
  },
  {
    question: "How can I join Doxzon as a healthcare provider?",
    answer:
      "Healthcare providers can join by registering on Doxzon, meeting platform-specific requirements, and completing the necessary verification steps.",
  },
];

export function FAQ() {
  const dark = useDarkMode();
  const isMobile = useIsMobile();
  const [openIndex, setOpenIndex] = useState(0);

  const t = dark
    ? {
        sectionBg: "#0a0800",
        labelText: "#f0c040",
        headingText: "#f5e6b0",
        subText: "#b89a4a",
        cardBg: "#110d02",
        cardBorderOpen: "rgba(200,150,30,0.45)",
        cardBorderClosed: "rgba(200,150,30,0.15)",
        numGrad: "linear-gradient(135deg,#c8961e,#7a5c10)",
        numShadow: "0 4px 12px rgba(200,150,30,0.35)",
        questionText: "#f5e6b0",
        chevronOpen: { bg: "#c8961e", color: "#fff", border: "#c8961e" },
        chevronClosed: { bg: "transparent", color: "#b89a4a", border: "rgba(200,150,30,0.35)" },
        divider: "rgba(200,150,30,0.15)",
        answerText: "#b89a4a",
      }
    : {
        sectionBg: "#eef8fb",
        labelText: "#14b8a6",
        headingText: "#0f172a",
        subText: "#64748b",
        cardBg: "#ffffff",
        cardBorderOpen: "#99f6e4",
        cardBorderClosed: "#e2e8f0",
        numGrad: "linear-gradient(135deg,#22d3ee,#14b8a6)",
        numShadow: "0 4px 12px rgba(6,182,212,0.30)",
        questionText: "#1e293b",
        chevronOpen: { bg: "#032b4a", color: "#fff", border: "#032b4a" },
        chevronClosed: { bg: "transparent", color: "#64748b", border: "#cbd5e1" },
        divider: "#e2e8f0",
        answerText: "#64748b",
      };

  return (
    <section style={{
      background: t.sectionBg,
      padding: isMobile ? "3rem 0 4rem" : "6rem 0",
      transition: "background 0.4s",
      overflowX: "hidden",
    }}>
      <div style={{
        maxWidth: "56rem",
        margin: "0 auto",
        padding: isMobile ? "0 16px" : "0 1.5rem",
        boxSizing: "border-box",
        width: "100%",
      }}>

        {/* Header */}
        <div style={{ textAlign: "center" }}>
          <span style={{
            fontSize: isMobile ? 11 : 13,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            color: t.labelText,
          }}>
            FAQ
          </span>

          <h2 style={{
            marginTop: 12,
            fontSize: isMobile ? "1.6rem" : "clamp(1.8rem,3.5vw,2.5rem)",
            fontWeight: 700,
            color: t.headingText,
            lineHeight: 1.2,
          }}>
            Patient Help Center
          </h2>

          <p style={{
            marginTop: 10,
            color: t.subText,
            fontSize: isMobile ? 13 : 15,
            lineHeight: 1.6,
          }}>
            Everything you need to know about Doxzon and modern virtual healthcare
          </p>
        </div>

        {/* FAQ List */}
        <div style={{
          marginTop: isMobile ? 28 : 56,
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 8 : 16,
        }}>
          {FAQS.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                style={{
                  overflow: "hidden",
                  borderRadius: isMobile ? 12 : 28,
                  border: `1px solid ${isOpen ? t.cardBorderOpen : t.cardBorderClosed}`,
                  background: t.cardBg,
                  boxShadow: isOpen
                    ? dark ? "0 4px 20px rgba(200,150,30,0.12)" : "0 4px 20px rgba(20,184,166,0.08)"
                    : "0 1px 4px rgba(0,0,0,0.06)",
                  transition: "border 0.3s, box-shadow 0.3s, background 0.4s",
                }}
              >
                {/* Question row */}
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  style={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: isMobile ? "12px 12px" : "20px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16, minWidth: 0 }}>
                    {/* Number badge */}
                    <div style={{
                      flexShrink: 0,
                      width: isMobile ? 26 : 32,
                      height: isMobile ? 26 : 32,
                      borderRadius: "50%",
                      background: t.numGrad,
                      boxShadow: t.numShadow,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: isMobile ? 9 : 11,
                      fontWeight: 700,
                      color: "#fff",
                    }}>
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    <h3 style={{
                      fontSize: isMobile ? 13 : 17,
                      fontWeight: 600,
                      color: t.questionText,
                      margin: 0,
                      lineHeight: 1.35,
                      /* prevent long words from overflowing */
                      wordBreak: "break-word",
                    }}>
                      {faq.question}
                    </h3>
                  </div>

                  {/* Chevron */}
                  <div style={{
                    flexShrink: 0,
                    width: isMobile ? 28 : 40,
                    height: isMobile ? 28 : 40,
                    borderRadius: "50%",
                    border: `1px solid ${isOpen ? t.chevronOpen.border : t.chevronClosed.border}`,
                    background: isOpen ? t.chevronOpen.bg : t.chevronClosed.bg,
                    color: isOpen ? t.chevronOpen.color : t.chevronClosed.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s, background 0.3s, border 0.3s",
                  }}>
                    <ChevronDown size={isMobile ? 14 : 18} color="currentColor" />
                  </div>
                </button>

                {/* Answer */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28 }}
                    >
                      <div style={{ padding: isMobile ? "0 12px 12px 12px" : "0 64px 24px" }}>
                        <div style={{ height: 1, background: t.divider, marginBottom: isMobile ? 8 : 16 }} />
                        <p style={{
                          fontSize: isMobile ? 11 : 14,
                          lineHeight: 1.65,
                          color: t.answerText,
                          margin: 0,
                        }}>
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}